import { useEffect } from "react";
import { useTranslation } from "../i18n";
import { useAppSelector } from "../redux/hooks";
import type { ResolvedTenant } from "../services/tenantService";
import {
  applyDocumentFavicon,
  defaultDocumentTitle,
  persistDocumentBranding,
  readStoredDocumentBranding,
  resolveBrandingFaviconUrl,
  resolveBrandingTitle,
  shouldReplaceDocumentTitle,
} from "../utils/tenantBranding";
import { useResolvedTenant } from "./useResolvedTenant";

/**
 * Applies tenant/company favicon and default document title site-wide
 * (dashboard, auth, and storefront). Page-specific titles still come from
 * `usePageTitle`.
 */
export function useDocumentBranding(tenant?: ResolvedTenant | null): void {
  const { language } = useTranslation();
  const { data: resolvedTenant } = useResolvedTenant();
  const company = useAppSelector((state) => state.company);
  const branding = tenant ?? resolvedTenant;
  const stored = readStoredDocumentBranding();

  const faviconHref =
    resolveBrandingFaviconUrl(branding) ??
    resolveBrandingFaviconUrl({
      faviconUrl: company.faviconUrl,
      logoUrl: company.logoUrl,
    }) ??
    stored?.faviconHref ??
    null;

  const brandName =
    resolveBrandingTitle(branding, language) ??
    resolveBrandingTitle(
      { name: company.name, nameAr: company.nameAr },
      language,
    ) ??
    resolveBrandingTitle(stored, language) ??
    defaultDocumentTitle(language);

  const cachedTitle = resolveBrandingTitle(stored, language);

  useEffect(() => {
    if (!faviconHref) return;
    applyDocumentFavicon(faviconHref);
  }, [faviconHref]);

  useEffect(() => {
    if (shouldReplaceDocumentTitle(document.title, brandName, cachedTitle)) {
      document.title = brandName;
    }
  }, [brandName, cachedTitle]);

  useEffect(() => {
    const name = branding?.name?.trim() || company.name.trim();
    const nameAr = (branding?.nameAr ?? company.nameAr)?.trim() || null;
    if (!name && !nameAr && !faviconHref) return;
    persistDocumentBranding({
      name: name || nameAr || "",
      nameAr,
      faviconHref,
    });
  }, [
    branding?.name,
    branding?.nameAr,
    company.name,
    company.nameAr,
    faviconHref,
  ]);
}
